import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { formatPhone } from '../utils/formatPhone';

const STATUS_META = {
  pending:   { color: '#60a5fa', bg: '#1e3a5f', label: 'Scheduled' },
  sent:      { color: '#4ade80', bg: '#1a3a2a', label: 'Sent' },
  failed:    { color: '#f87171', bg: '#2a1f1f', label: 'Failed' },
  cancelled: { color: '#94a3b8', bg: '#1e293b', label: 'Cancelled' },
};

const TYPE_META = {
  user_requested: { color: '#c084fc', label: 'User Requested' },
  auto_checkin:   { color: '#60a5fa', label: 'Auto Check-in' },
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function FollowUps() {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('pending');
  const [selected, setSelected] = useState(null);
  const [editing, setEditing]   = useState(false);
  const [editMsg, setEditMsg]   = useState('');
  const [editDate, setEditDate] = useState('');
  const [saving, setSaving]     = useState(false);

  // Manual create
  const [showCreate, setShowCreate] = useState(false);
  const [newForm, setNewForm] = useState({ phone: '', scheduledAt: '', message: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}` : '';
      const { data } = await api.get(`/api/follow-ups${params}`);
      setItems(data.followUps);
      setTotal(data.total);
    } catch { setItems([]); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function cancel(id) {
    if (!window.confirm('Cancel this follow-up?')) return;
    try {
      const { data } = await api.patch(`/api/follow-ups/${id}/cancel`);
      setItems(prev => prev.map(i => i._id === id ? data : i));
      if (selected?._id === id) setSelected(data);
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  }

  async function reschedule(id) {
    setSaving(true);
    try {
      const { data } = await api.patch(`/api/follow-ups/${id}/reschedule`, {
        scheduledAt: new Date(editDate).toISOString(),
        message: editMsg,
      });
      setItems(prev => prev.map(i => i._id === id ? data : i));
      setSelected(data);
      setEditing(false);
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    setSaving(false);
  }

  async function createManual(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post('/api/follow-ups', {
        ...newForm,
        scheduledAt: new Date(newForm.scheduledAt).toISOString(),
      });
      setItems(prev => [data, ...prev]);
      setTotal(t => t + 1);
      setShowCreate(false);
      setNewForm({ phone: '', scheduledAt: '', message: '' });
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    setSaving(false);
  }

  const counts = items.reduce((a, i) => { a[i.status] = (a[i.status] || 0) + 1; return a; }, {});

  return (
    <div style={{ padding: 32, background: '#0f172a', minHeight: '100vh', color: '#f1f5f9' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>Follow-up Queue</h1>
          <p style={{ fontSize: 14, color: '#64748b' }}>{total} follow-ups • Auto check-in every 3 days of silence</p>
        </div>
        <button onClick={() => setShowCreate(true)} style={{ padding: '10px 20px', borderRadius: 10, background: '#ef4444', border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          + Schedule Follow-up
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 14, marginBottom: 24 }}>
        {Object.entries(STATUS_META).map(([k, v]) => (
          <div key={k} style={{ background: '#1e293b', borderRadius: 12, padding: '14px 18px', border: `1px solid ${v.color}20` }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: v.color }}>{counts[k] || 0}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{v.label}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {[['', 'All'], ['pending', 'Scheduled'], ['sent', 'Sent'], ['failed', 'Failed'], ['cancelled', 'Cancelled']].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} style={{
            padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            border: `1px solid ${filter === k ? '#ef4444' : '#334155'}`,
            background: filter === k ? '#ef444420' : 'transparent',
            color: filter === k ? '#ef4444' : '#64748b',
          }}>{l}</button>
        ))}
        <button onClick={load} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #334155', background: 'transparent', color: '#64748b', fontSize: 13, cursor: 'pointer', marginLeft: 'auto' }}>↻</button>
      </div>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        {/* Table */}
        <div style={{ flex: 1, background: '#1e293b', borderRadius: 16, border: '1px solid #334155', overflow: 'hidden', minWidth: 0 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#475569' }}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#475569' }}>No follow-ups found.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  {['Contact', 'Type', 'Scheduled', 'Status', 'Message Preview', ''].map(h => (
                    <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 11, color: '#475569', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(fu => {
                  const sm = STATUS_META[fu.status];
                  const tm = TYPE_META[fu.type];
                  const isSel = selected?._id === fu._id;
                  return (
                    <tr key={fu._id}
                      onClick={() => { setSelected(fu); setEditing(false); setEditMsg(fu.message); setEditDate(fu.scheduledAt?.slice(0, 16)); }}
                      style={{ borderBottom: '1px solid #1e293b', cursor: 'pointer', background: isSel ? '#263548' : 'transparent' }}
                      onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#243040'; }}
                      onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>{fu.leadName || formatPhone(fu.phone)}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{formatPhone(fu.phone)}</div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${tm?.color}18`, color: tm?.color }}>{tm?.label}</span>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtDate(fu.scheduledAt)}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: sm?.bg, color: sm?.color }}>{sm?.label}</span>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 12, color: '#64748b', maxWidth: 200 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fu.message}</div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        {fu.status === 'pending' && (
                          <button onClick={e => { e.stopPropagation(); cancel(fu._id); }} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13, opacity: 0.7 }}>Cancel</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div style={{ width: 310, flexShrink: 0, background: '#1e293b', borderRadius: 16, border: '1px solid #334155', padding: 22 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700 }}>Detail</h3>
              <button onClick={() => setSelected(null)} style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
            </div>

            {[
              ['Contact', selected.leadName || '—'],
              ['Phone', formatPhone(selected.phone)],
              ['Type', TYPE_META[selected.type]?.label],
              ['Status', STATUS_META[selected.status]?.label],
              ['Scheduled', fmtDate(selected.scheduledAt)],
              ['Sent at', fmtDate(selected.sentAt)],
            ].map(([l, v]) => (
              <div key={l} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{l}</div>
                <div style={{ fontSize: 13, color: '#e2e8f0' }}>{v || '—'}</div>
              </div>
            ))}

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Message</div>
              {editing ? (
                <textarea value={editMsg} onChange={e => setEditMsg(e.target.value)} rows={4}
                  style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 13, padding: '8px 10px', resize: 'vertical', fontFamily: 'inherit' }}
                />
              ) : (
                <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.6, background: '#0f172a', borderRadius: 8, padding: '10px 12px' }}>{selected.message}</div>
              )}
            </div>

            {selected.status === 'pending' && (
              editing ? (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 10, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>New Date & Time</div>
                    <input type="datetime-local" value={editDate} onChange={e => setEditDate(e.target.value)}
                      style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 13, padding: '8px 10px' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => reschedule(selected._id)} disabled={saving} style={{ flex: 1, padding: '9px', borderRadius: 8, background: '#ef4444', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={() => setEditing(false)} style={{ padding: '9px 14px', borderRadius: 8, background: 'transparent', border: '1px solid #334155', color: '#94a3b8', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setEditing(true)} style={{ flex: 1, padding: '9px', borderRadius: 8, background: '#1e3a5f', border: 'none', color: '#60a5fa', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Edit & Reschedule</button>
                  <button onClick={() => cancel(selected._id)} style={{ padding: '9px 12px', borderRadius: 8, background: 'transparent', border: '1px solid #ef444440', color: '#ef4444', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                </div>
              )
            )}

            {selected.error && (
              <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, background: '#2a1f1f', border: '1px solid #ef444430', fontSize: 12, color: '#f87171' }}>
                Error: {selected.error}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Manual create modal */}
      {showCreate && (
        <div onClick={e => { if (e.target === e.currentTarget) setShowCreate(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#1e293b', borderRadius: 20, border: '1px solid #334155', padding: 32, width: '100%', maxWidth: 440 }}>
            <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 20 }}>Schedule Follow-up</h3>
            <form onSubmit={createManual} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[['Phone (with country code)', 'phone', 'text', '919876543210'],
                ['Schedule Date & Time', 'scheduledAt', 'datetime-local', '']].map(([label, field, type, ph]) => (
                <div key={field}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 5 }}>{label}</label>
                  <input type={type} placeholder={ph} value={newForm[field]} onChange={e => setNewForm(f => ({ ...f, [field]: e.target.value }))} required
                    style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 14, padding: '10px 12px' }}
                  />
                </div>
              ))}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: 5 }}>Message</label>
                <textarea rows={3} value={newForm.message} onChange={e => setNewForm(f => ({ ...f, message: e.target.value }))} required
                  style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 14, padding: '10px 12px', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" disabled={saving} style={{ flex: 1, padding: '11px', borderRadius: 10, background: '#ef4444', border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  {saving ? 'Scheduling…' : 'Schedule →'}
                </button>
                <button type="button" onClick={() => setShowCreate(false)} style={{ padding: '11px 16px', borderRadius: 10, background: 'transparent', border: '1px solid #334155', color: '#94a3b8', fontSize: 14, cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
